import { parseISO, isAfter } from "date-fns";

/**
 * Get the current date and time in Saudi Arabia timezone (GMT+3)
 * @returns {Date} Current date and time in Saudi Arabia timezone
 */
export const getCurrentSaudiTime = () => {
  // Get current time in Saudi Arabia timezone
  const now = new Date();
  
  // Convert to Saudi time using toLocaleString which handles DST properly
  const saudiTimeString = now.toLocaleString("en-CA", {
    timeZone: "Asia/Riyadh",
    year: "numeric",
    month: "2-digit", 
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  });
  
  // Convert the string back to a Date object
  const saudiTime = new Date(saudiTimeString.replace(", ", "T"));
  
  return saudiTime;
};

/**
 * Check if an event is expired based on its end date/time
 * Compares against current Saudi Arabia time (GMT+3)
 * @param {Object} event - Event object
 * @returns {boolean} True if event is expired, false otherwise
 */
export const isEventExpired = (event) => {
  if (!event) return true;

  const currentSaudiTime = getCurrentSaudiTime();

  // Check if event has endDate property
  if (event.endDate) {
    try {
      const eventEndDate = typeof event.endDate === 'string' 
        ? parseISO(event.endDate) 
        : new Date(event.endDate);
      
      // Since we're dealing with Saudi time, compare directly
      return isAfter(currentSaudiTime, eventEndDate);
    } catch (error) {
      console.warn('Error parsing event endDate:', error);
      // Return false for invalid dates to be safe
      return false;
    }
  }

  // Check time_table for the latest end time if no endDate
  if (event.time_table && Array.isArray(event.time_table)) {
    try {
      // Find the latest end time from all time slots
      const latestEndTime = event.time_table.reduce((latest, timeSlot) => {
        if (!timeSlot.date || !timeSlot.end_time) return latest;
        
        // Check if end_time already has seconds (format: HH:mm:ss vs HH:mm)
        const hasSeconds = timeSlot.end_time.split(':').length === 3;
        const dateTimeString = hasSeconds 
          ? `${timeSlot.date}T${timeSlot.end_time}` 
          : `${timeSlot.date}T${timeSlot.end_time}:00`;
        
        const slotDateTime = new Date(dateTimeString);
        
        // Validate the parsed date
        if (isNaN(slotDateTime.getTime())) {
          console.warn(`Invalid date parsed: ${dateTimeString}`);
          return latest;
        }
        
        // Since API returns times in Saudi timezone, treat them as such
        // Compare directly without additional timezone conversion
        return !latest || isAfter(slotDateTime, latest) ? slotDateTime : latest;
      }, null);

      if (latestEndTime) {
        return isAfter(currentSaudiTime, latestEndTime);
      }
    } catch (error) {
      console.warn('Error parsing event time_table:', error);
      // Return false for parsing errors to be safe
      return false;
    }
  }

  // If no end date information available, consider not expired
  return false;
};

/**
 * Filter out expired events from an array of events
 * @param {Array} events - Array of event objects
 * @returns {Array} Array of non-expired events
 */
export const filterExpiredEvents = (events) => {
  if (!Array.isArray(events)) return events;
  
  return events.filter(event => !isEventExpired(event));
};

/**
 * Check if a specific time slot is expired
 * @param {string} date - Date string (YYYY-MM-DD format)
 * @param {string} endTime - End time string (HH:mm format)
 * @returns {boolean} True if time slot is expired, false otherwise
 */
export const isTimeSlotExpired = (date, endTime) => {
  if (!date || !endTime) return true;

  try {
    const currentSaudiTime = getCurrentSaudiTime();
    
    // Check if endTime already has seconds (format: HH:mm:ss vs HH:mm)
    const hasSeconds = endTime.split(':').length === 3;
    const dateTimeString = hasSeconds 
      ? `${date}T${endTime}` 
      : `${date}T${endTime}:00`;
    
    // Create the slot end datetime - treat as Saudi timezone since API returns Saudi times
    const slotEndDateTime = new Date(dateTimeString);
    
    // Validate the parsed date
    if (isNaN(slotEndDateTime.getTime())) {
      console.warn(`Invalid date parsed in isTimeSlotExpired: ${dateTimeString}`);
      return true; // Consider expired if parsing fails
    }
    
    // Since API returns times in Saudi timezone, compare directly
    return isAfter(currentSaudiTime, slotEndDateTime);
  } catch (error) {
    console.warn('Error parsing time slot:', error);
    return true; // Consider expired if parsing fails
  }
};